import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Clock, ArrowLeft, ChevronDown } from "lucide-react";
import usePublicContent from "../hooks/usePublicContent";
import { applySeoMeta, injectJsonLd, removeJsonLd } from "../lib/seoMeta";
import { useLangPrefix } from "../lib/langRoutes";
import { canonicalizeBlogSlug, blogSlugFor, usePublicPaths } from "../lib/localizedSlugs";
import { PublicNav, PublicCta, PublicFooter } from "../components/app/PublicChrome";

function Faq({ items }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(null);
  if (!items?.length) return null;
  return (
    <div className="mt-12">
      <h2 className="font-display text-2xl font-bold text-white mb-5">{t("blogUi.faqTitle")}</h2>
      <div className="border border-slate-800 divide-y divide-slate-800">
        {items.map((f, i) => (
          <div key={f.q} className="bg-slate-950/60">
            <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left" data-testid={`article-faq-${i}`}>
              <span className="font-medium text-slate-100">{f.q}</span>
              <ChevronDown size={16} className={`text-cyan-400 flex-shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`}/>
            </button>
            {open === i && <div className="px-5 pb-4 text-sm text-slate-400 leading-relaxed">{f.a}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BlogArticlePage() {
  const { slug: rawSlug } = useParams();
  const slug = canonicalizeBlogSlug(rawSlug) || rawSlug;
  const { t } = useTranslation();
  const lp = useLangPrefix();
  const pp = usePublicPaths();
  const lang = lp.slice(1);
  const articles = usePublicContent("blog");
  const article = articles?.find((a) => a.slug === slug);

  useEffect(() => {
    if (!article) return;
    applySeoMeta({
      title: `${article.title} · MachinePilot Blog`,
      description: article.metaDesc,
      path: `/blog/${slug}`,
      ogImage: "/og-cover.jpg",
      pathForLang: (lng) => `/blog/${blogSlugFor(slug, lng)}`,
    });
    injectJsonLd("jsonld-article", {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.metaDesc,
      datePublished: article.date,
      author: { "@type": "Organization", name: "MachinePilot" },
      publisher: { "@type": "Organization", name: "MachinePilot", url: "https://machinepilot.eu" },
      mainEntityOfPage: `https://machinepilot.eu/blog/${article.slug}`,
    });
    if (article.faq?.length) {
      injectJsonLd("jsonld-article-faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: article.faq.map((f) => ({
          "@type": "Question", name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
    return () => { removeJsonLd("jsonld-article"); removeJsonLd("jsonld-article-faq"); };
  }, [article, slug]);

  const related = articles?.filter((a) => a.slug !== slug).slice(0, 3) || [];

  const expected = lang ? blogSlugFor(slug, lang) : slug;
  if (rawSlug !== expected) return <Navigate to={`${lp}/blog/${expected}`} replace />;

  return (
    <div className="theme-swiss min-h-screen bg-slate-950 text-slate-100" data-testid={`blog-article-${slug}`}>
      <PublicNav />
      {!article ? (
        <div className="max-w-3xl mx-auto px-6 py-24 text-slate-500" data-testid="article-loading">…</div>
      ) : (
        <>
          <article className="max-w-3xl mx-auto px-6 pt-12 pb-4">
            <Link to={`${lp}/blog`} className="text-sm text-slate-500 hover:text-cyan-400 flex items-center gap-1 mb-6" data-testid="back-to-blog">
              <ArrowLeft size={14}/> {t("blogUi.backToBlog")}
            </Link>
            <div className="flex items-center gap-4 mb-4">
              <span className="label-tech text-cyan-400">{article.category}</span>
              <span className="text-xs text-slate-500 flex items-center gap-1"><Clock size={12}/> {article.readingTime} {t("blogUi.minRead")}</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4" data-testid="article-title">{article.title}</h1>
            <p className="text-lg text-slate-400 leading-relaxed border-l-2 border-cyan-500/40 pl-4">{article.excerpt}</p>
            <div className="mt-10 space-y-10">
              {article.sections.map((s) => (
                <section key={s.h2}>
                  <h2 className="font-display text-2xl font-bold text-white mb-4">{s.h2}</h2>
                  {s.paragraphs?.map((p) => (
                    <p key={p} className="text-slate-300 leading-relaxed mb-4">{p}</p>
                  ))}
                  {s.list && (
                    <ul className="space-y-2 mt-2">
                      {s.list.map((li) => (
                        <li key={li} className="flex items-start gap-2 text-slate-300 leading-relaxed">
                          <span className="text-cyan-400 mt-1.5 text-[9px]">■</span> <span>{li}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>
            <Faq items={article.faq} />
          </article>
          {article.cta && <PublicCta title={article.cta.title} body={article.cta.body} button={article.cta.button} />}
          {related.length > 0 && (
            <section className="max-w-6xl mx-auto px-6 pb-10">
              <h2 className="label-tech text-slate-500 mb-4">{t("blogUi.related")}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {related.map((r) => (
                  <Link key={r.slug} to={pp.blog(r.slug)} className="tech-card p-5 group" data-testid={`related-${r.slug}`}>
                    <div className="label-tech text-cyan-400 mb-2">{r.category}</div>
                    <div className="font-medium text-white group-hover:text-cyan-400 transition-colors text-sm">{r.title}</div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
      <PublicFooter />
    </div>
  );
}
